import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, X, ImagePlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ImageUploaderProps {
  /** Storage bucket name. */
  bucket: string;
  /** Current image URL (signed or public). */
  value?: string;
  /** Called with the new public/signed URL after upload, or "" when cleared. */
  onChange: (url: string) => void;
  /** Optional prefix inside the user folder, e.g. "avatars" → uploads to {uid}/avatars/{file}. */
  folder?: string;
  /** Accepted MIME types — defaults to images. */
  accept?: string;
  /** Max size in MB. Default 5. */
  maxMb?: number;
  /** Aspect ratio for preview box, default "video" (16:9). */
  aspect?: "video" | "square";
  label?: string;
}

export function ImageUploader({
  bucket,
  value,
  onChange,
  folder,
  accept = "image/png,image/jpeg,image/webp,image/gif",
  maxMb = 5,
  aspect = "video",
  label = "Upload image",
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function handleFile(file: File) {
    if (!file.type.startsWith("image/")) {
      return toast.error("Please pick an image file");
    }
    if (file.size > maxMb * 1024 * 1024) {
      return toast.error(`Image must be under ${maxMb}MB`);
    }
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return toast.error("Sign in first");

    setBusy(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
      const ts = Date.now();
      const rand = Math.random().toString(36).slice(2, 8);
      const key = [u.user.id, folder, `${ts}-${rand}.${ext}`].filter(Boolean).join("/");

      const { error: upErr } = await supabase.storage
        .from(bucket)
        .upload(key, file, { upsert: false, contentType: file.type, cacheControl: "31536000" });
      if (upErr) throw upErr;

      // Try public URL first; if the bucket is private, fall back to a long-lived signed URL.
      const { data: pub } = supabase.storage.from(bucket).getPublicUrl(key);
      let url = pub.publicUrl;
      // Probe: if public URL returns 400 (private bucket), use signed URL instead.
      try {
        const head = await fetch(url, { method: "HEAD" });
        if (!head.ok) throw new Error("not public");
      } catch {
        const { data: signed, error: sErr } = await supabase.storage
          .from(bucket)
          .createSignedUrl(key, 60 * 60 * 24 * 365); // 1 year
        if (sErr) throw sErr;
        url = signed.signedUrl;
      }
      onChange(url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error((e as Error).message || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const boxRatio = aspect === "square" ? "aspect-square" : "aspect-video";

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />

      {value ? (
        <div className={`relative w-full ${boxRatio} rounded-xl overflow-hidden bg-muted border border-border`}>
          <img src={value} alt="Uploaded preview" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-background/80 backdrop-blur border border-border hover:bg-background"
            aria-label="Remove image"
          >
            <X className="h-4 w-4" />
          </button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="absolute bottom-2 right-2"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
            Replace
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className={`w-full ${boxRatio} rounded-xl border-2 border-dashed border-border bg-muted/30 hover:bg-muted/60 transition-colors grid place-items-center text-muted-foreground`}
        >
          {busy ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <div className="flex flex-col items-center gap-1.5">
              <ImagePlus className="h-7 w-7" />
              <span className="text-xs font-medium">{label}</span>
              <span className="text-[10px]">PNG, JPG, WebP · up to {maxMb}MB</span>
            </div>
          )}
        </button>
      )}
    </div>
  );
}
